// components/feed/FeedSection.js - COMPLETE FIXED VERSION
import React from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import useFeedData from '../../hooks/useFeedData';
import { generateFeedUrlFromApiEndpoint } from '../../utils/feedUrlUtils'; 
import { isCrawlFeed } from '../../utils/crawlFeedAdapter';
import FeedItem from './FeedItem';
import './FeedSection.css';

// isCrawlFeed expects a bare param string; apiEndpoint may carry a leading &
// or ? depending on which config produced it.
const isCrawlBackedFeed = (apiEndpoint) => isCrawlFeed((apiEndpoint || '').replace(/^[&?]+/, ''));

// Turn a human-readable collection name into the snake_case table identifier
// (e.g. "Suprema Corte de Justicia de la Nación" -> "suprema_corte_de_justicia_de_la_nacion").
function slugifyCollection(name) {
  return (name || '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[’'`.,()]/g, '')
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
}

// Prefer an explicit `source`; otherwise derive the table from `collection_name`;
// finally fall back to the aggregation table parsed from the endpoint.
function recentsTableFor(item, fallbackTable) {
  if (item && item.source) return item.source;
  const slug = slugifyCollection(item && item.collection_name);
  return slug || fallbackTable;
}

/**
 * FeedSection component - Displays a section of feed items from a specific source
 * 
 * @param {Object} props
 * @param {string} props.title - Section title
 * @param {string} props.apiEndpoint - API endpoint for the feed
 * @param {string} props.feedName - Name of the feed
 * @param {string} props.branch - Branch (Legislative, Executive, etc.)
 * @param {string} props.topic - Topic category
 * @param {string} [props.buttonName="View"] - Text for item buttons
 * @param {string} props.viewAllLink - Link to view all content
 * @param {string} props.titleLink - Link for the title/heading click (e.g., agency page)
 * @param {Function} [props.displayFunction] - Optional custom display function
 * @param {number} [props.maxItems=1] - Maximum number of items to display, defaults to 1
 * @param {boolean} [props.showViewMore=true] - Whether to show View More button in FeedItem (desktop only)
 * @param {boolean} [props.isRecentsItem=false] - Whether this is a recents item (shows source header in FeedItem)
 * @param {string} [props.recentsSchema=null] - For recents only: the country schema to route items to
 *   (e.g. 'mexico'). Recents are fetched from public/news_items, but each item belongs to this
 *   schema, so clicks must resolve to /feed/{recentsSchema}/{item.source} rather than public/news_items.
 */
const FeedSection = ({
  title,
  apiEndpoint,
  feedName,
  branch,
  topic,
  buttonName = "View",
  viewAllLink,
  titleLink,
  displayFunction,
  maxItems = 1,
  showViewMore = false,
  isRecentsItem = false,
  recentsSchema = null
}) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  
  // Use our custom hook to fetch data
  const { data, loading, error } = useFeedData(apiEndpoint, maxItems);

  // FIXED: Enhanced URL generation function
  const generateFeedUrl = (apiEndpoint) => {
    // Crawl feeds (crawl=recent) have no schema/table, so every branch below
    // would fall through to /feed/{schema}/default — a route with nothing
    // behind it. Returning null hands the decision to viewAllLink instead,
    // which is how StatePage points View More at its own Updates view.
    if (isCrawlBackedFeed(apiEndpoint)) return null;

    try {
      const urlData = generateFeedUrlFromApiEndpoint(apiEndpoint);
      if (urlData) {
        return urlData.fullUrl;
      }
      
      // Fallback for malformed endpoints
      console.warn('Could not generate URL for endpoint, using fallback:', apiEndpoint);
      
      // Clean the endpoint for fallback parsing
      let cleanParams = apiEndpoint;
      if (cleanParams.startsWith('&') || cleanParams.startsWith('?')) {
        cleanParams = cleanParams.substring(1);
      }
      
      const urlParams = new URLSearchParams(cleanParams);
      const schema = urlParams.get('schema') || 'united_states_of_america';
      const table = urlParams.get('table') || urlParams.get('table_name') || 'default';
      
      return `/feed/${schema}/${table}`;
    } catch (error) {
      console.error('Error in generateFeedUrl:', error);
      return null;
    }
  };

  // FIXED: Handle title click - prioritize titleLink, then generated URL, then viewAllLink
  const handleTitleClick = (e) => {
    e.preventDefault();
    
    // If titleLink is provided, use it (e.g., for agency pages)
    if (titleLink) {
      navigate(titleLink);
      return;
    }

    // Recents aggregates public/news_items; the heading must never route there.
    // Send it to the country recents page instead.
    if (isRecentsItem) {
      if (viewAllLink) navigate(viewAllLink);
      return;
    }
    
    const feedUrl = generateFeedUrl(apiEndpoint);
    
    if (feedUrl) {
      navigate(feedUrl);
    } else if (viewAllLink) {
      navigate(viewAllLink);
    } else {
      console.error('No valid URL generated for title click:', apiEndpoint);
    }
  };

  // Generate the View More URL for FeedItem (non-recents only; recents is per-item below).
  // For crawl feeds generateFeedUrl is null, so this resolves to viewAllLink —
  // and to nothing at all when the caller supplied neither, which leaves
  // FeedItem's own `showViewMore && viewMoreUrl` gate to hide the button.
  const viewMoreUrl = generateFeedUrl(apiEndpoint) || viewAllLink || null;

  // Return loading state
  if (loading) {
    return (
      <div className="feed-section">
        {title && (
          <h4 
            id='sectionHeading' 
            className="section-title"
            onClick={handleTitleClick}
            style={{ cursor: 'pointer' }}
          >
            {title}
            <ChevronRight id="clickChevron" />
          </h4>
        )}
        <div className="loading-placeholder">
          <div className="feed-section-loading">
            <Spinner animation="grow" variant="light" size='sm' />
          </div>
        </div>
      </div>
    );
  }

  // Return error state
  if (error) {
    return (
      <div className="feed-section">
        {title && (
          <h4 
            id='sectionHeading' 
            className="section-title"
            onClick={handleTitleClick}
            style={{ cursor: 'pointer' }}
          >
            {title}
            <ChevronRight id="clickChevron" />
          </h4>
        )}
        <div className="feed-section-error">
          {t('common:errorLoadingContent', 'Error loading content')}: {error}
          <div style={{ marginTop: '10px' }}>
            <Button variant="outline-light" size="sm" onClick={() => window.location.reload()}>
              {t('common:tryAgain', 'Try Again')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Handle empty data
  if (!data || data.length === 0) {
    return (
      <div className="feed-section">
        {title && (
          <h4 
            id='sectionHeading' 
            className="section-title"
            onClick={handleTitleClick}
            style={{ cursor: 'pointer' }}
          >
            {title}
            <ChevronRight id="clickChevron" />
          </h4>
        )}
        <div className="feed-section-empty">
          {t('common:noContentAvailable', 'No content available')}
          {apiEndpoint && (
            <div style={{ fontSize: '12px', marginTop: '10px', opacity: 0.7 }}>
             
            </div>
          )}
        </div>
      </div>
    );
  }

  // FIXED: Extract schema and table for FeedItem routing with better error handling
  let schema = 'united_states_of_america';
  let table = 'default';
  
  try {
    // Clean the endpoint
    let cleanEndpoint = apiEndpoint;
    if (cleanEndpoint.startsWith('&') || cleanEndpoint.startsWith('?')) {
      cleanEndpoint = cleanEndpoint.substring(1);
    }
    
    const urlParams = new URLSearchParams(cleanEndpoint);
    schema = urlParams.get('schema') || 'united_states_of_america';
    table = urlParams.get('table') || urlParams.get('table_name') || 'default';
  } catch (error) {
    console.error('Error parsing apiEndpoint for FeedItem:', error);
  }

  return (
    <div className="feed-section">
      {title && (
        <h4 
          id='sectionHeading' 
          className="section-title"
          onClick={handleTitleClick}
          style={{ cursor: 'pointer' }}
        >
          {title}
          <ChevronRight id="clickChevron" />
        </h4>
      )}

      {/* If a custom display function is provided, use it, otherwise use default rendering */}
      {displayFunction ? (
        displayFunction(data.slice(0, maxItems))
      ) : (
        <div className="feed-items">
          {data.slice(0, maxItems).map((item, index) => {
            // For recents items, get feedName from the item's data
            const displayFeedName = isRecentsItem 
              ? (item.collection_name || item.source || feedName)
              : feedName;

            // Recents: route to the country schema (recentsSchema), not the public
            // aggregation schema, and use the item's own source as the table.
            const displaySchema = isRecentsItem
              ? (recentsSchema || schema)
              : schema;
            const displayTable = isRecentsItem
              ? recentsTableFor(item, table)
              : table;

            // View More for recents points at the item's source feed in the country schema.
            const itemViewMoreUrl = isRecentsItem
              ? `/feed/${displaySchema}/${displayTable}`
              : viewMoreUrl;

            return (
              <FeedItem
                key={`${item.id || index}-${displaySchema}-${displayTable}`}
                item={item}
                feedName={displayFeedName}
                branch={branch}
                topic={topic}
                buttonName={buttonName}
                schema={displaySchema}
                table={displayTable}
                variant="homepage"
                showViewMore={showViewMore}
                viewMoreUrl={showViewMore ? itemViewMoreUrl : null}
                isRecentsItem={isRecentsItem}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FeedSection;